import {
	Button,
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	Chip,
	Divider,
	Image,
	Link,
	Spacer,
	Tooltip,
} from "@heroui/react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import {
	ArrowRight,
	Check,
	Download,
	MonitorSmartphone,
	ShieldCheck,
	Sparkles,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import SiteFooter from "../../components/SiteFooter";
import BasicNavbar from "./BasicNavbar";
import FAQSection from "./FAQSection";

const MOTION_PROPS = {
	initial: { opacity: 0, y: 24, filter: "blur(8px)" },
	animate: { opacity: 1, y: 0, filter: "blur(0px)" },
	transition: { type: "spring", stiffness: 120, damping: 18 } as any,
};

const SCROLL_SECTION_PROPS = {
	initial: { opacity: 0, y: 32, filter: "blur(8px)" },
	whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
	viewport: { once: true, amount: 0.3 },
	transition: { type: "spring", stiffness: 120, damping: 20 } as any,
};

// Scroll-in variants for non-hero sections: header + staggered children
const VIEWPORT = { once: true, amount: 0.18 } as const;

const scrollSectionHeader = {
	initial: { opacity: 0, y: 28, filter: "blur(6px)" },
	whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
	viewport: VIEWPORT,
	transition: { type: "spring" as const, stiffness: 100, damping: 22 },
};

const scrollCardUp = {
	initial: { opacity: 0, y: 36, filter: "blur(4px)" },
	whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
	viewport: VIEWPORT,
	transition: { type: "spring" as const, stiffness: 100, damping: 20 },
};

const scrollCardLeft = {
	initial: { opacity: 0, x: -28, y: 12 },
	whileInView: { opacity: 1, x: 0, y: 0 },
	viewport: VIEWPORT,
	transition: { type: "spring" as const, stiffness: 90, damping: 18 },
};

const scrollCardRight = {
	initial: { opacity: 0, x: 28, y: 12 },
	whileInView: { opacity: 1, x: 0, y: 0 },
	viewport: VIEWPORT,
	transition: { type: "spring" as const, stiffness: 90, damping: 18 },
};

const scrollCardScale = {
	initial: { opacity: 0, scale: 0.96, y: 20 },
	whileInView: { opacity: 1, scale: 1, y: 0 },
	viewport: VIEWPORT,
	transition: { type: "spring" as const, stiffness: 110, damping: 20 },
};

const scrollFooterBar = {
	initial: { opacity: 0, y: 24 },
	whileInView: { opacity: 1, y: 0 },
	viewport: VIEWPORT,
	transition: {
		type: "spring" as const,
		stiffness: 80,
		damping: 18,
		delay: 0.15,
	},
};

function HeroVideo() {
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		const v = videoRef.current;
		if (!v) return;
		v.play().catch(() => {});
	}, []);

	return (
		<div className="relative aspect-[16/9] w-full overflow-hidden rounded-b-lg">
			<video
				ref={videoRef}
				className="h-full w-full object-cover [&::-webkit-media-controls-panel]:bg-transparent"
				src="/videos/hero-3.webm"
				controls
				controlsList="nodownload"
				autoPlay
				loop
				playsInline
				aria-label="3ChatClaw 演示"
			/>
		</div>
	);
}

export default function StationLandingPage() {
	return (
		<div className="dark">
			<div className="bg-background relative flex min-h-screen min-h-dvh w-full flex-col overflow-x-hidden overflow-y-auto scroll-smooth">
				<BasicNavbar />

				<main className="relative z-10 flex w-full flex-1 flex-col items-center pt-24 pb-40">
					{/* 背景渐变 */}
					<div className="pointer-events-none absolute inset-x-0 top-0 z-0 flex justify-center">
						<div className="bg-primary/30 from-primary/40 to-sky-500/10 h-[320px] w-[560px] translate-y-[-40%] rounded-full bg-[radial-gradient(circle_at_top,_var(--tw-gradient-from),_transparent_60%)] blur-3xl" />
					</div>

					{/* Hero */}
					<LazyMotion features={domAnimation}>
						<section className="relative z-10 w-full max-w-5xl px-6 sm:px-8">
							<m.div
								{...MOTION_PROPS}
								transition={{ ...MOTION_PROPS.transition, delay: 0.05 }}
								className="flex flex-col items-center text-center"
							>
								<img
									src="/images/3ChatClawLandingPage/3ChatClaw_logo.png"
									alt="3ChatClaw"
									className="h-6 w-auto object-contain sm:h-8"
								/>

								<Spacer y={6} />

								<h1 className="text-foreground text-[clamp(36px,8vw,48px)] leading-tight font-bold tracking-tight sm:text-[56px]">
									<span>一键开干的AI数字员工！</span>
								</h1>

								<Spacer y={4} />

								<p className="text-default-500 max-w-2xl text-base leading-relaxed sm:text-lg">
									商家专属数字员工，本地部署Openclaw🦞，100%
									保障账号与数据资产安全
									<br />
									无需代码一键启动预置实战工作流技能（Skill），按转化效果扣费，不花冤枉钱
								</p>

								<Spacer y={6} />

								<m.div
									{...MOTION_PROPS}
									transition={{ ...MOTION_PROPS.transition, delay: 0.15 }}
									className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-center sm:gap-4"
								>
									{/* <Button
                    as={Link}
                    href="https://app.3chatai.com/butler/login?utm_source=3chatclaw"
                    color="primary"
                    className="text-sm font-medium cursor-pointer"
                    radius="full"
                    endContent={<ArrowRight className="h-4 w-4" />}
                  >
                    免费注册
                  </Button> */}
									<Button
										as={Link}
										href="https://app.3chatai.com/butler/3chatclaw/download"
										className="bg-foreground text-background text-sm font-medium cursor-pointer px-5"
										radius="full"
										// endContent={<ArrowRight className="h-4 w-4" />}
										variant="flat"
										endContent={<Download className="h-4 w-4" />}
									>
										立即下载
									</Button>
								</m.div>
							</m.div>

							<Spacer y={10} />

							{/* 能力概览三卡片 */}
							<m.div
								{...MOTION_PROPS}
								transition={{ ...MOTION_PROPS.transition, delay: 0.22 }}
								className="grid grid-cols-1 gap-4 md:grid-cols-3"
							>
								<CapabilityCard
									icon={<ShieldCheck className="h-6 w-6 text-success" />}
									title="本地部署，绝对安全可控"
									description={
										"真实宽带IP和物理网络拓扑\n保障数据安全，与业务连续性"
									}
								/>
								<CapabilityCard
									icon={<MonitorSmartphone className="h-6 w-6 text-danger" />}
									title="3ChatClaw塞进手机，无死角获客"
									description={
										"Mac/PC+接管手机，从桌面端到移动端的\n全场景渗透流量平台"
									}
								/>
								<CapabilityCard
									icon={<Sparkles className="h-6 w-6 text-primary" />}
									title="预置实战技能，开箱即用"
									description={
										"预置不断丰富的小红书、抖音等\n多套实战自动化工作流技能”"
									}
								/>
							</m.div>

							<Spacer y={20} />

							{/* 产品截图/视频 */}
							<m.div
								{...MOTION_PROPS}
								transition={{ ...MOTION_PROPS.transition, delay: 0.3 }}
								className="relative w-full max-w-5xl"
							>
								<Card className="bg-default-100/5 border-default-100 relative overflow-hidden border">
									<CardHeader className="flex items-center gap-2 border-b border-default-100/40 px-4 py-3">
										<div className="flex items-center gap-1.5">
											<span className="h-2 w-2 rounded-full bg-danger-400" />
											<span className="h-2 w-2 rounded-full bg-warning-400" />
											<span className="h-2 w-2 rounded-full bg-success-400" />
										</div>
										<div className="bg-default-50/5 text-default-500 ml-4 flex flex-1 items-center justify-center rounded-full px-3 py-1 text-[11px] sm:text-xs">
											3ChatClaw/Instructions
										</div>
									</CardHeader>
									<CardBody className="relative h-full p-0">
										<div className="relative h-full w-full">
											<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.24),_transparent_60%),_radial-gradient(circle_at_bottom,_rgba(59,130,246,0.24),_transparent_60%)]" />
											<HeroVideo />
										</div>
									</CardBody>
								</Card>
							</m.div>

							<Spacer y={4} />

							<m.div
								{...MOTION_PROPS}
								transition={{ ...MOTION_PROPS.transition, delay: 0.34 }}
								className="mt-4 flex w-full items-center justify-center"
							>
								<Button
									as={Link}
									href="https://site.3chatai.cn/blog"
									isExternal
									variant="flat"
									radius="full"
									size="sm"
									className="border-default-100/60 bg-foreground/5 text-xs font-medium text-default-500 hover:border-primary/70 hover:bg-foreground/20 hover:text-foreground cursor-pointer"
								>
									更多视频
								</Button>
							</m.div>
						</section>
					</LazyMotion>

					{/* 痛点对照区块 */}
					<LazyMotion features={domAnimation}>
						<section className="relative z-10 mt-40 w-full max-w-5xl px-6 sm:mt-40 sm:px-8">
							<m.div
								{...scrollSectionHeader}
								className="flex flex-col items-center text-center"
							>
								<div className="bg-primary/10 border-primary/40 text-primary inline-flex items-center gap-2 rounded-full border px-3 py-1 text-small font-medium tracking-[0.16em] uppercase">
									<span className="bg-primary h-1.5 w-1.5 rounded-full" />
									<span>痛点对照</span>
								</div>

								<Spacer y={6} />

								<h2 className="text-foreground text-2xl font-semibold tracking-tight sm:text-[32px]">
									为什么需要 3ChatClaw
								</h2>

								<p className="text-default-500 mt-3 max-w-4xl text-sm leading-relaxed sm:text-base">
									告别脆弱的云端工具。在物理世界，为您的生意构建一套安全、全能且即刻开工的
									AI 自动化底座
									{/* <br />
                  两个死穴，两款产品专门来解。 */}
								</p>
							</m.div>

							<div className="mt-10 grid grid-cols-1 gap-4">
								{/* 01 本地部署 · 安全可控 */}
								<m.div
									{...scrollCardUp}
									transition={{ ...scrollCardUp.transition, delay: 0.06 }}
									whileHover={{ y: -6, scale: 1.01 }}
									style={{ willChange: "transform" }}
								>
									<Card className="bg-default-100/10 border-default-100/60 relative overflow-hidden rounded-2xl border">
										<CardBody className="relative flex h-full flex-col gap-4 px-5 py-5 sm:flex-row sm:items-start sm:gap-5 sm:px-6 sm:py-6">
											<div className="flex flex-shrink-0 items-stretch">
												<div className="bg-default-100/20 border-default-100/60 relative h-24 w-24 overflow-hidden rounded-xl border sm:h-28 sm:w-28">
													<Image
														src="/images/3ChatClawLandingPage/section1-1.png"
														alt="安全基座"
													/>
												</div>
											</div>
											<div className="flex flex-1 flex-col gap-4 text-left">
												<div className="flex items-start justify-between gap-3">
													<div className="inline-flex items-center gap-2 rounded-full bg-success/10 px-3 py-1 text-[11px] font-medium text-success-400">
														<span className="bg-success-400 h-1.5 w-1.5 rounded-full" />
														<span>01 · 安全基座</span>
													</div>
													<div className="bg-success/10 text-success-400 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full">
														<ShieldCheck className="h-4 w-4" />
													</div>
												</div>

												<div className="flex flex-col gap-1.5">
													<p className="text-foreground text-base font-semibold sm:text-lg">
														本地部署，绝对安全可控
													</p>
													<p className="text-default-500 text-xs sm:text-sm">
														与其在云端赌账号的命，不如把 AI
														关进自己的物理安全屋。
													</p>
												</div>

												<div className="bg-default-100/40 h-px w-full" />

												<div className="flex flex-col gap-2 text-xs sm:text-sm text-default-500">
													<p>
														3ChatClaw 部署在本地 Mac mini 或 PC
														硬件上，使用真实宽带 IP 和物理网络拓扑，
														让自动化动作不再依赖脆弱的云端节点。
														{/* </p>
                          <p> */}
														核心业务数据不出内网，既保障账号资产安全，又为高客单价生意提供稳定、可控的执行底座。
													</p>
												</div>
											</div>
										</CardBody>
									</Card>
								</m.div>

								{/* 02 全域平台渗透 · 无死角获客 */}
								<m.div
									{...scrollCardUp}
									transition={{ ...scrollCardUp.transition, delay: 0.12 }}
									whileHover={{ y: -6, scale: 1.01 }}
									style={{ willChange: "transform" }}
								>
									<Card className="bg-default-100/10 border-default-100/60 relative overflow-hidden rounded-2xl border">
										<CardBody className="relative flex h-full flex-col gap-4 px-5 py-5 sm:flex-row sm:items-start sm:gap-5 sm:px-6 sm:py-6">
											<div className="flex flex-shrink-0 items-stretch">
												<div className="bg-default-100/20 border-default-100/60 relative h-24 w-24 overflow-hidden rounded-xl border sm:h-28 sm:w-28">
													<Image
														src="/images/3ChatClawLandingPage/section1-2.png"
														alt="全域渗透"
													/>
												</div>
											</div>
											<div className="flex flex-1 flex-col gap-4 text-left">
												<div className="flex items-start justify-between gap-3">
													<div className="inline-flex items-center gap-2 rounded-full bg-danger/10 px-3 py-1 text-[11px] font-medium text-danger">
														<span className="bg-danger h-1.5 w-1.5 rounded-full" />
														<span>02 · 全域渗透</span>
													</div>
													<div className="bg-danger/10 text-danger flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full">
														<MonitorSmartphone className="h-4 w-4" />
													</div>
												</div>

												<div className="flex flex-col gap-1.5">
													<p className="text-foreground text-base font-semibold sm:text-lg">
														3ChatClaw塞进手机，无死角获客
													</p>
													<p className="text-default-500 text-xs sm:text-sm">
														突破复杂接口限制，把AI销冠塞进手机，让获客再无‘禁区’。
													</p>
												</div>

												<div className="bg-default-100/40 h-px w-full" />

												<div className="flex flex-col gap-2 text-xs sm:text-sm text-default-500">
													<p>
														不再受限于“官方不开放
														API”。通过接管物理手机，3ChatClaw 赋予了 AI 深入 App
														内部执行任务的能力。只要人手能完成的滑动、点击与对话，3ChatClaw
														都能在流量最密集的社交深水区，为您全自动化获客。
													</p>
												</div>
											</div>
										</CardBody>
									</Card>
								</m.div>

								{/* 03 预置 Skill 合集 · 开箱即用 */}
								<m.div
									{...scrollCardUp}
									transition={{ ...scrollCardUp.transition, delay: 0.18 }}
									whileHover={{ y: -6, scale: 1.01 }}
									style={{ willChange: "transform" }}
								>
									<Card className="bg-default-100/10 border-default-100/60 relative overflow-hidden rounded-2xl border">
										<CardBody className="relative flex h-full flex-col gap-4 px-5 py-5 sm:flex-row sm:items-start sm:gap-5 sm:px-6 sm:py-6">
											<div className="flex flex-shrink-0 items-stretch">
												<div className="bg-default-100/20 border-default-100/60 relative h-24 w-24 overflow-hidden rounded-xl border sm:h-28 sm:w-28">
													<Image
														src="/images/3ChatClawLandingPage/section1-3.png"
														alt="预置 Skill"
													/>
												</div>
											</div>
											<div className="flex flex-1 flex-col gap-4 text-left">
												<div className="flex items-start justify-between gap-3">
													<div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-medium text-primary-500">
														<span className="bg-primary-500 h-1.5 w-1.5 rounded-full" />
														<span>03 · 预置 Skill</span>
													</div>
													<div className="bg-primary/10 text-primary-500 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full">
														<Sparkles className="h-4 w-4" />
													</div>
												</div>

												<div className="flex flex-col gap-1.5">
													<p className="text-foreground text-base font-semibold sm:text-lg">
														预置 Skill 合集，开箱即用
													</p>
													<p className="text-default-500 text-xs sm:text-sm">
														别再纠结 Prompt 怎么写，只管下达你想要的生意结果。
													</p>
												</div>

												<div className="bg-default-100/40 h-px w-full" />

												<div className="flex flex-col gap-2 text-xs sm:text-sm text-default-500">
													<p>
														内置来自一线运营团队的实战经验与平台规则，沉淀成一套不断更新的
														Skill 合集， 覆盖小红书、抖音等核心阵地。
													</p>
													<p>
														无需写代码、也不用自己训
														AI，像招“地推专员”和“流量狙击手”一样，
														用效果说话，按转化结果付费。
													</p>
												</div>
											</div>
										</CardBody>
									</Card>
								</m.div>
							</div>
						</section>
					</LazyMotion>

					<Spacer y={16} />

					{/* 双引擎闭环：4 步完成从截流到结单 */}
					<LazyMotion features={domAnimation}>
						<section className="relative z-10 mt-40 w-full max-w-6xl px-6 sm:mt-40 sm:px-8">
							<m.div
								{...scrollSectionHeader}
								className="flex flex-col items-center text-center"
							>
								<div className="bg-primary/10 border-primary/40 text-primary inline-flex items-center gap-2 rounded-full border px-3 py-1 text-small font-medium tracking-[0.16em] uppercase">
									<span className="bg-primary h-1.5 w-1.5 rounded-full" />
									<span>双引擎闭环</span>
								</div>

								<Spacer y={6} />

								<h2 className="text-foreground text-2xl font-semibold tracking-tight sm:text-[32px]">
									为什么选择 3ChatClaw
								</h2>

								<p className="text-default-500 mt-3 max-w-2xl text-sm leading-relaxed sm:text-base">
									用 4 步流程，每一步都有“输入→输出”
								</p>
							</m.div>

							<div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-4">
								<m.div
									{...scrollCardScale}
									transition={{ ...scrollCardScale.transition, delay: 0.06 }}
									style={{ willChange: "transform", cursor: "pointer" }}
									onClick={() => {
										window.open("https://group.3chatai.cn/c/23-category/23");
									}}
								>
									<EngineStepCard
										step="01"
										badge="截流"
										badgeColor="primary"
										image="/images/3ChatClawLandingPage/section2-1.png"
										title="截流"
										description="3ChatClaw 在竞品评论区/同城热帖里找目标客户"
										product="3ChatClaw"
										productTone="primary"
									/>
								</m.div>
								<m.div
									{...scrollCardScale}
									transition={{ ...scrollCardScale.transition, delay: 0.12 }}
									style={{ willChange: "transform", cursor: "pointer" }}
									onClick={() => {
										window.open("https://group.3chatai.cn/c/23-category/23");
									}}
								>
									<EngineStepCard
										step="02"
										badge="导流"
										badgeColor="success"
										image="/images/3ChatClawLandingPage/section2-2.png"
										title="导流"
										description="引导加微信（合规话术模板 + 多方案）"
										product="3ChatClaw"
										productTone="success"
									/>
								</m.div>
								<m.div
									{...scrollCardScale}
									transition={{ ...scrollCardScale.transition, delay: 0.18 }}
									style={{ willChange: "transform", cursor: "pointer" }}
									onClick={() => {
										window.open("https://group.3chatai.cn/c/23-category/23");
									}}
								>
									<EngineStepCard
										step="03"
										badge="接管"
										badgeColor="warning"
										image="/images/3ChatClawLandingPage/section2-3.png"
										title="接管"
										description="客户进微信，3Chat.ai 自动接待"
										product="3Chat.ai"
										productTone="warning"
									/>
								</m.div>
								<m.div
									{...scrollCardScale}
									transition={{ ...scrollCardScale.transition, delay: 0.24 }}
									style={{ willChange: "transform", cursor: "pointer" }}
									onClick={() => {
										window.open("https://group.3chatai.cn/c/23-category/23");
									}}
								>
									<EngineStepCard
										step="04"
										badge="结单"
										badgeColor="danger"
										image="/images/3ChatClawLandingPage/section2-4.png"
										title="结单"
										description="逼单/预约/收定金（你只看结果）"
										product="3Chat.ai"
										productTone="danger"
									/>
								</m.div>
							</div>

							<m.div
								{...scrollFooterBar}
								className="mt-10 flex items-center justify-center gap-2 text-center text-[11px] text-default-500 sm:text-xs"
							>
								<div className="bg-default-100/40 h-px w-16 sm:w-32" />
								<span>全程无需人工干预，老板只看结果报表</span>
								<div className="bg-default-100/40 h-px w-16 sm:w-32" />
							</m.div>
						</section>
					</LazyMotion>

					{/* 结果承诺：行业轮播案例 */}
					<LazyMotion features={domAnimation}>
						<section className="relative z-10 mt-40 w-full max-w-6xl px-6 sm:mt-40 sm:px-8">
							<m.div
								{...scrollSectionHeader}
								className="flex flex-col items-center text-center"
							>
								<div className="bg-warning/10 border-warning/40 text-warning inline-flex items-center gap-2 rounded-full border px-3 py-1 text-small font-medium tracking-[0.16em] uppercase">
									<span className="bg-warning h-1.5 w-1.5 rounded-full" />
									<span>结果承诺</span>
								</div>

								<Spacer y={4} />

								<h2 className="text-foreground text-2xl font-semibold tracking-tight sm:text-[32px]">
									跨行业验证的真实增长数据
								</h2>

								<p className="text-default-500 mt-3 max-w-3xl text-sm leading-relaxed sm:text-base">
									3ChatClaw 已服务超过 2,000+
									企业客户，覆盖本地生活、电商、医美大健康、教育培训等核心赛道。
								</p>
							</m.div>

							<m.div
								{...scrollCardUp}
								transition={{ ...scrollCardUp.transition, delay: 0.1 }}
								className="mt-10"
							>
								<ResultCarousel />
							</m.div>
						</section>
					</LazyMotion>

					{/* 风控与安全：三重保障 */}
					<LazyMotion features={domAnimation}>
						<section className="relative z-10 mt-40 w-full max-w-6xl px-6 sm:mt-40 sm:px-8">
							<m.div
								{...scrollSectionHeader}
								className="flex flex-col items-center text-center"
							>
								<div className="bg-success/10 border-success/40 text-success-400 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-small font-medium tracking-[0.16em] uppercase">
									<span className="bg-success-400 h-1.5 w-1.5 rounded-full" />
									<span>风控与安全</span>
								</div>

								<Spacer y={4} />

								<h2 className="text-foreground text-2xl font-semibold tracking-tight sm:text-[32px]">
									保号 · 保密 · 保合规
								</h2>

								<p className="text-default-500 mt-3 max-w-2xl text-sm leading-relaxed sm:text-base">
									高客单价生意，安全是第一条件。账号、数据、合规，每一层都有明确边界。
								</p>
							</m.div>

							<div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
								<m.div
									{...scrollCardUp}
									transition={{ ...scrollCardUp.transition, delay: 0.06 }}
									style={{ willChange: "transform" }}
								>
									<RiskCard
										tone="danger"
										title="账号安全"
										subtitle="节奏控制 · 异常暂停 · 黑名单"
										description="3ChatClaw 内置行为节奏引擎，模拟真人操作频率，杜绝平台风控触发。"
										bullets={[
											"行为节奏控制，拟人化操作间隔",
											"异常自动暂停，保护账号资产",
											"黑名单过滤，精准避开无效用户",
										]}
									/>
								</m.div>
								<m.div
									{...scrollCardUp}
									transition={{ ...scrollCardUp.transition, delay: 0.12 }}
									style={{ willChange: "transform" }}
								>
									<RiskCard
										tone="primary"
										title="数据安全"
										subtitle="本地运行 · 云端权限 · 操作审计"
										description="3ChatClaw 部署于本地工作站，核心数据不出门；云端按需授权，全程可审计。"
										bullets={[
											"边缘本地运行，核心数据不上云",
											"云端最小权限原则，按需授权",
											"完整操作日志，全链路可审计",
										]}
									/>
								</m.div>
								<m.div
									{...scrollCardUp}
									transition={{ ...scrollCardUp.transition, delay: 0.18 }}
									style={{ willChange: "transform" }}
								>
									<RiskCard
										tone="success"
										title="安全合规底线"
										subtitle="不碰敏感行为 · 明确边界"
										description="我们只做平台规则允许的自动化动作，明确划定红线，让老板用得安心。"
										bullets={[
											"不做刷量、诱导、虚假行为",
											"平台红线清单，操作边界透明",
											"合规话术模板，规避账号风险",
										]}
									/>
								</m.div>
							</div>

							<m.div
								{...scrollFooterBar}
								className="bg-default-100/5 border-default-100/40 mt-8 flex flex-wrap items-center justify-center gap-4 rounded-2xl border px-4 py-4 text-[11px] text-default-500 sm:gap-6 sm:px-6 sm:text-xs"
							>
								<div className="flex items-start gap-2">
									<span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-success-400" />
									<div>
										<p className="text-foreground text-[11px] font-semibold sm:text-xs">
											本地部署
										</p>
										<p>数据不出本地</p>
									</div>
								</div>
								<div className="hidden h-6 w-px bg-default-100/40 sm:block" />
								<div className="flex items-start gap-2">
									<span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-primary" />
									<div>
										<p className="text-foreground text-[11px] font-semibold sm:text-xs">
											账号自有
										</p>
										<p>平台账号归您所有</p>
									</div>
								</div>
								<div className="hidden h-6 w-px bg-default-100/40 sm:block" />
								<div className="flex items-start gap-2">
									<span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-warning-400" />
									<div>
										<p className="text-foreground text-[11px] font-semibold sm:text-xs">
											操作透明
										</p>
										<p>每一步日志可追溯</p>
									</div>
								</div>
								<div className="hidden h-6 w-px bg-default-100/40 sm:block" />
								<div className="flex items-start gap-2">
									<span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-danger-400" />
									<div>
										<p className="text-foreground text-[11px] font-semibold sm:text-xs">
											合规边界
										</p>
										<p>红线清单公开承诺</p>
									</div>
								</div>
							</m.div>
						</section>
					</LazyMotion>

					<LandingPricingSection />
					<FAQSection />
				</main>
				<SiteFooter />
			</div>
		</div>
	);
}

type CapabilityCardProps = {
	icon: React.ReactNode;
	title: string;
	description: string;
};

type PainPointItemProps = {
	icon: React.ReactNode;
	title: string;
};

type EngineStepCardProps = {
	step: string;
	badge: string;
	badgeColor: "danger" | "warning" | "primary" | "success";
	image: string;
	title: string;
	description: string;
	product: string;
	productTone: "danger" | "warning" | "primary" | "success";
};

type ResultCase = {
	id: string;
	label: string;
	industry: string;
	image: string;
	metrics: Array<{
		label: string;
		value: string;
		tone: "success" | "primary" | "secondary" | "danger";
	}>;
};

type RiskCardProps = {
	tone: "danger" | "primary" | "success";
	title: string;
	subtitle: string;
	description: string;
	bullets: string[];
};

const STAGGER_DELAYS = [0.06, 0.12, 0.18, 0.24];

function LandingPricingSection() {
	return (
		<LazyMotion features={domAnimation}>
			<section
				className="relative z-10 mt-40 w-full max-w-6xl px-6 sm:mt-40 sm:px-8"
				id="pricing"
			>
				<m.div
					{...scrollSectionHeader}
					className="relative z-10 flex flex-col items-center text-center"
				>
					<div className="bg-primary/10 border-primary/40 text-primary inline-flex items-center gap-2 rounded-full border px-3 py-1 text-small font-medium tracking-[0.16em] uppercase">
						<span className="bg-primary h-1.5 w-1.5 rounded-full" />
						<span>价格与套餐</span>
					</div>

					<Spacer y={4} />

					<h2 className="text-foreground text-2xl font-semibold tracking-tight sm:text-[32px]">
						透明定价，按需选择
					</h2>

					<p className="text-default-500 mt-3 max-w-2xl text-sm leading-relaxed sm:text-base">
						硬件一次性投入，软件按月订阅（送 4,000 Credits），无隐藏费用
					</p>
				</m.div>

				<Spacer y={20} />

				{/* 套餐卡片：灵活组合 / 全包套装 */}
				<div className="relative z-10 mx-auto grid w-full max-w-5xl grid-cols-1 gap-4 lg:grid-cols-2">
					{/* 顶部渐变光圈，呼应整页视觉 */}
					<div className="pointer-events-none absolute inset-x-0 -top-24 z-0 flex justify-center">
						<div className="bg-primary/40 from-primary/40 to-sky-500/10 h-64 w-[520px] -translate-y-10 rounded-full bg-[radial-gradient(circle_at_top,_var(--tw-gradient-from),_transparent_60%)] blur-3xl" />
					</div>

					{/* 全包套装 - 推荐方案 */}
					<m.div
						{...scrollCardScale}
						transition={{ ...scrollCardScale.transition, delay: 0.12 }}
						whileHover={{ y: -10 }}
						style={{ willChange: "transform" }}
						className="h-full"
					>
						<Card className="bg-default-100/10 border-primary/60 relative flex h-full flex-col rounded-2xl border shadow-xl shadow-primary/10 overflow-visible">
							<div className="absolute inset-x-0 -top-3 flex justify-center">
								<Chip
									size="sm"
									color="default"
									className="bg-primary/90 text-xs text-foreground shadow-md"
								>
									推荐方案
								</Chip>
							</div>

							<CardHeader className="flex flex-col items-start gap-3 px-6 pt-8 pb-6">
								<h3 className="text-foreground text-2xl font-regular">
									⭐️ 软件订阅（自带设备*）
								</h3>
								<div>
									<div className="flex items-baseline gap-2">
										<span className="text-foreground text-4xl font-bold tracking-tight">
											¥ 198
										</span>
										<span className="text-default-500 text-sm"> /月</span>
									</div>
									<p className="text-default-500 mt-1 text-sm">
										含 4,000 Credits/月，每月固定赠送基础用量，当月有效
									</p>
								</div>
							</CardHeader>
							<Divider />
							<CardBody className="flex flex-1 flex-col gap-3 px-6 py-5">
								{[
									{
										label: "官方 Skill 运行授权",
										description:
											"解锁商机雷达、流量截胡、金牌销冠等全套 AI 技能权限。",
									},
									{
										label: "ADB 链路打通",
										description: "接管手机终端。",
									},
									{
										label: "云端大脑连接",
										description:
											"支持外挂专业知识库，实现 AI 意图识别与精准回复。",
									},
									{
										label: "基础算力赠送",
										description:
											" 包含首月基础 Credit 算力包，足够跑通初期业务流。",
									},
									{
										label: "软件安装培训",
										description: "线上指导 3ChatClaw 安装。",
									},
								].map((item) => (
									<div
										key={item.label}
										className="flex flex-col gap-1 text-sm text-default-500"
									>
										<div className="flex items-center gap-2">
											<span className="bg-primary/20 text-primary flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
												<Check className="h-3.5 w-3.5" />
											</span>
											<Tooltip
												content={
													<span className="text-s leading-relaxed text-foreground">
														{item.description}
													</span>
												}
												showArrow
												placement="top-start"
												className="max-w-xs bg-default-100/95"
											>
												<span className="text-left border-b border-dashed border-default-400/70 pb-[1px] cursor-help">
													{item.label}
												</span>
											</Tooltip>
										</div>
									</div>
								))}
							</CardBody>
							<CardFooter className="px-6 pb-6 pt-2">
								<Button
									fullWidth
									as={Link}
									variant="bordered"
									radius="sm"
									className="border-default-100/60 bg-transparent text-sm font-medium text-foreground transition-colors hover:border-transparent hover:bg-foreground hover:text-background cursor-pointer"
									endContent={<ArrowRight className="h-4 w-4" />}
									href="https://app.3chatai.com/butler/login?utm_source=3chatclaw"
								>
									立即订阅
								</Button>
							</CardFooter>
						</Card>
					</m.div>

					{/* 灵活组合 */}
					<m.div
						{...scrollCardLeft}
						transition={{ ...scrollCardLeft.transition, delay: 0.08 }}
						whileHover={{ y: -8 }}
						style={{ willChange: "transform" }}
						className="h-full"
					>
						<Card className="bg-default-100/10 border-default-100/60 relative flex h-full flex-col overflow-hidden rounded-2xl border">
							<CardHeader className="flex flex-col items-start gap-3 px-6 pt-8 pb-6">
								<h3 className="text-foreground text-2xl font-regular">
									全包套装 (开箱即用)
								</h3>
								<div>
									<div className="flex items-baseline gap-2">
										<span className="text-foreground text-4xl font-bold tracking-tight">
											¥ 6,999
										</span>
										<span className="text-default-500 text-sm">一次性</span>
									</div>
									<p className="text-default-500 mt-1 text-sm">
										{" "}
										含1年订阅费用（¥1,980/年）
									</p>
								</div>
							</CardHeader>
							<Divider />
							<CardBody className="flex flex-1 flex-col gap-3 px-6 py-5">
								{[
									{
										label: "专属独立物理主机+手机",
										description:
											"包含一台Mac mini 主机+红米安卓机（芯片M4 、内存16G、存储容量 256GB）出厂已为您完美预装 3ChatClaw 与所有底层环境。",
									},
									{
										label: "纯净运行环境",
										description:
											"为数字员工提供 24 小时不断电、不休眠的独立运行空间，绝不干扰您日常的办公电脑。",
									},
									{
										label: "专家级 1v1 实施部署",
										description:
											"专属工程师线上协助您完成首次网络连通、微信/企微通道绑定及首个 Skill 的跑通。",
									},
									{
										label: "尊享算力包与服务",
										description:
											"赠送超大额 Credit 算力点，并享有全年优先客服响应（SLA保障）。",
									},
								].map((item) => (
									<div
										key={item.label}
										className="flex flex-col gap-1 text-sm text-default-500"
									>
										<div className="flex items-center gap-2">
											<span className="bg-success/20 text-success-400 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
												<Check className="h-3.5 w-3.5" />
											</span>
											<Tooltip
												content={
													<span className="text-s leading-relaxed text-foreground">
														{item.description}
													</span>
												}
												showArrow
												placement="top-start"
												className="max-w-xs bg-default-100/95"
											>
												<span className="text-left border-b border-dashed border-default-400/70 pb-[1px] cursor-help">
													{item.label}
												</span>
											</Tooltip>
										</div>
									</div>
								))}
							</CardBody>
							<CardFooter className="px-6 pb-6 pt-2">
								<Button
									fullWidth
									as={Link}
									variant="bordered"
									radius="sm"
									className="border-default-100/60 bg-transparent text-sm font-medium text-foreground transition-colors hover:border-transparent hover:bg-foreground hover:text-background cursor-pointer"
									endContent={<ArrowRight className="h-4 w-4" />}
									href="https://app.3chatai.com/butler/login?utm_source=3chatclaw"
								>
									选择套装
								</Button>
							</CardFooter>
						</Card>
					</m.div>

					{/* 流量包
          <m.div
            {...scrollCardRight}
            transition={{ ...scrollCardRight.transition, delay: 0.16 }}
            whileHover={{ y: -8 }}
            style={{ willChange: "transform" }}
            className="h-full"
          >
            <Card className="bg-default-100/10 border-default-100/60 relative flex h-full flex-col overflow-hidden rounded-2xl border">
              <CardHeader className="flex flex-col items-start gap-3 px-6 pt-6 pb-2">
                <h3 className="text-foreground text-2xl font-regular">流量包</h3>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-foreground text-4xl font-bold tracking-tight">按需</span>
                    <span className="text-default-500 text-sm">充值</span>
                  </div>
                </div>
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-1 flex-col gap-3 px-6 py-5">
                {[
                  "¥100 → 2000 Credits",
                  "¥500 → 10,000 Credits",
                  "¥2,000 → 42,000 Credits（含赠送）",
                  "¥10,000 → 220,000 Credits（含赠送）",
                  "有效期 12 个月",
                  "大额充值享赠送",
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-default-500">
                    <span className="bg-success/20 text-success-400 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-left">{feature}</span>
                  </div>
                ))}
              </CardBody>
              <CardFooter className="px-6 pb-6 pt-2">
                <Button
                  fullWidth
                  as={Link}
                  variant="bordered"
                  radius="sm"
                  className="border-default-100/60 text-sm"
                  endContent={<ArrowRight className="h-4 w-4" />}
                  href={`${window.location.origin}/topup`}
                >
                  查看流量包
                </Button>
              </CardFooter>
            </Card>
          </m.div> */}
				</div>

				{/* 设备型号与环境要求 */}
				<m.div
					{...scrollCardUp}
					transition={{ ...scrollCardUp.transition, delay: 0.18 }}
					className="relative z-10 mt-10"
				>
					<Card className="bg-default-100/10 border-default-100/60 mx-auto flex max-w-5xl flex-col gap-4 rounded-2xl border px-5 py-5 sm:px-6 sm:py-6">
						<div className="flex flex-col gap-2">
							<h4 className="text-foreground text-base font-medium">
								*设备型号与环境要求
							</h4>
							<p className="text-default-500 text-xs leading-relaxed sm:text-sm">
								为确保 3ChatClaw
								稳定运行，请参考下列推荐环境准备硬件与系统。如有特殊情况，可与我们团队沟通评估。
							</p>
						</div>
						<div className="overflow-x-auto">
							<table className="min-w-full border-collapse text-xs text-default-500 sm:text-sm">
								<thead>
									<tr className="border-b border-default-100/60 text-left text-foreground">
										<th className="whitespace-nowrap py-2 pr-6 font-medium">
											部署端
										</th>
										<th className="whitespace-nowrap py-2 pr-6 font-medium">
											硬件环境要求
										</th>
										<th className="whitespace-nowrap py-2 pr-2 font-medium">
											软件系统环境要求
										</th>
									</tr>
								</thead>
								<tbody className="align-top">
									<tr className="border-b border-default-100/40">
										<td className="py-3 pr-6 text-foreground">Mac mini</td>
										<td className="py-3 pr-6">
											芯片 M4，内存 16G，存储容量 256GB，约 4,499 RMB。
										</td>
										<td className="py-3 pr-2">
											需安装 Homebrew，Node.js ≥ 22，Git 等基础开发环境。
										</td>
									</tr>
									<tr className="border-b border-default-100/40">
										<td className="py-3 pr-6 text-foreground">
											Windows 工作站
										</td>
										<td className="py-3 pr-6">
											Windows 10 21H2 / Windows 11，4 核心及以上 CPU，内存
											16GB+，可用磁盘空间 ≥ 20GB，确保 Edge 浏览器版本在 146+
											以上。
										</td>
										<td className="py-3 pr-2">
											需支持虚拟化与 WSL2，安装 Node.js ≥ 22、Git、C++
											编译工具（如 VisualStudioSetup.exe）、CMake 等。
										</td>
									</tr>
									<tr>
										<td className="py-3 pr-6 text-foreground">手机端</td>
										<td className="py-3 pr-6">
											支持开启开发者模式，并可通过 USB 调试连接到工作站，主流
											Android 机型均可。
										</td>
										<td className="py-3 pr-2">
											Android 系统，开启开发者模式与 USB 调试，授权 ADB
											控制权限。
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</Card>
				</m.div>

				{/* 订阅说明 */}
				<m.div
					{...scrollCardUp}
					transition={{ ...scrollCardUp.transition, delay: 0.22 }}
					className="relative z-10 mt-10"
				>
					<Card className="bg-default-100/10 border-default-100/60 mx-auto flex w-full max-w-5xl flex-col gap-4 rounded-2xl border px-5 py-5 sm:px-6 sm:py-6">
						<h4 className="text-foreground text-base font-medium">计费说明</h4>
						<div className="gap-4 grid grid-cols-1 text-sm text-default-500 sm:grid-cols-2">
							{[
								"失败不扣费：只有平台返回“发送成功”才计费。",
								"重复不计费：自动重试或重复动作只按一次计费。",
								"先赠送后充值：优先消耗当月赠送额度",
							].map((item) => (
								<div key={item} className="flex items-start gap-2">
									<span className="bg-success/20 text-success-400 mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full">
										<Check className="h-3 w-3" />
									</span>
									<p className="text-left text-xs leading-relaxed sm:text-sm">
										{item}
									</p>
								</div>
							))}
						</div>
					</Card>
				</m.div>

				{/* 底部提示文案 */}
				{/* <m.p
          {...scrollFooterBar}
          className="text-default-500 relative z-10 mt-6 text-center text-xs sm:text-[13px]"
        >
          * 硬件质保 1 年，软件订阅随时可暂停或取消，无违约金。
        </m.p> */}
			</section>
		</LazyMotion>
	);
}

function CapabilityCard({ icon, title, description }: CapabilityCardProps) {
	return (
		<Card className="bg-default-100/10 border-default-100/60 border">
			<CardBody className="flex flex-col items-center gap-2 text-center">
				<div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg">
					{icon}
				</div>
				<span className="text-medium font-medium text-default-foreground">
					{title}
				</span>
				<p className="text-small text-default-500 whitespace-pre-line">
					{description}
				</p>
			</CardBody>
		</Card>
	);
}

function PainPointItem({ icon, title }: PainPointItemProps) {
	return (
		<div className="bg-default-50/5 border-default-100/40 flex items-center gap-3 rounded-xl border px-3.5 py-3 sm:px-4 sm:py-3.5">
			<div className="bg-default-100/10 text-primary flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg">
				{icon}
			</div>
			<p className="text-foreground text-sm font-medium">{title}</p>
		</div>
	);
}

function EngineStepCard({
	step,
	badge,
	badgeColor,
	image,
	title,
	description,
	product,
	productTone,
}: EngineStepCardProps) {
	const badgeColorClassMap: Record<EngineStepCardProps["badgeColor"], string> =
		{
			danger: "bg-danger text-danger-400",
			warning: "bg-warning text-warning-500",
			primary: "bg-primary text-primary-400",
			success: "bg-success text-success-400",
		};

	const badgeSoftColorClassMap: Record<
		EngineStepCardProps["badgeColor"],
		string
	> = {
		danger: "bg-danger/15 text-danger-400",
		warning: "bg-warning/15 text-warning-500",
		primary: "bg-primary/15 text-primary",
		success: "bg-success/15 text-success-400",
	};

	const productToneClassMap: Record<
		EngineStepCardProps["productTone"],
		string
	> = {
		danger: "text-danger-400",
		warning: "text-warning-500",
		primary: "text-primary",
		success: "text-success-400",
	};

	return (
		<m.div
			whileHover={{ y: -4 }}
			transition={{ type: "spring", stiffness: 260, damping: 20 }}
			style={{ willChange: "transform" }}
		>
			<Card className="bg-default-100/10 border-default-100/60 relative flex h-full flex-col overflow-hidden rounded-2xl border">
				<div className="relative aspect-square w-full">
					<Image
						removeWrapper
						alt={title}
						className="h-full w-full object-cover"
						src={image}
					/>
					<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />

					<div className="absolute left-4 top-3 flex items-center gap-2 text-[11px]">
						<span
							className={`font-mono font-semibold ${productToneClassMap[productTone]}`}
						>
							{step}
						</span>
						<span
							className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeSoftColorClassMap[badgeColor]}`}
						>
							{badge}
						</span>
					</div>
				</div>

				<CardBody className="flex flex-1 flex-col gap-3 px-4 py-4 sm:px-5 sm:py-5">
					<div className="space-y-1 text-left h-[72px]">
						<p className="text-foreground text-sm font-semibold sm:text-base">
							{title}
						</p>
						<p className="text-default-500 text-xs leading-relaxed sm:text-[13px]">
							{description}
						</p>
					</div>
				</CardBody>

				<CardFooter className="border-default-100/40 flex items-center justify-between border-t px-4 py-3 text-[11px] text-default-500 sm:px-5">
					<span className={productToneClassMap[productTone]}>{product}</span>
					<div className="flex items-center gap-1 text-[10px]">
						<span className="bg-default-100/40 h-[1px] w-8" />
						<span>AI 全自动</span>
					</div>
				</CardFooter>
			</Card>
		</m.div>
	);
}

const RESULT_CASES: ResultCase[] = [
	{
		id: "local-life",
		label: "Local Life",
		industry: "本地生活",
		image: "/images/3ChatClawLandingPage/section3-1.png",
		metrics: [
			{ label: "夜间线索留存率", value: "+70%", tone: "danger" },
			{ label: "单店进店客流", value: "20+/日", tone: "danger" },
			{ label: "账号存活周期", value: "物理独立IP，稳定无忧", tone: "primary" },
		],
	},
	{
		id: "ecommerce",
		label: "E-Commerce",
		industry: "电商零售",
		image: "/images/3ChatClawLandingPage/section3-2.png",
		metrics: [
			{ label: "有效询价成本(CPL)", value: "-80%", tone: "success" },
			{ label: "私信破冰回复率", value: "+460%", tone: "danger" },
			{ label: "每日线索捕获量", value: "10倍规模化", tone: "danger" },
		],
	},
	{
		id: "healthcare",
		label: "Healthcare",
		industry: "医美大健康",
		image: "/images/3ChatClawLandingPage/section3-3.png",
		metrics: [
			{ label: "咨询转预约率", value: "+190%", tone: "danger" },
			{ label: "客服管理成本", value: "-70%", tone: "success" },
			{ label: "私域加粉率", value: "+175%", tone: "danger" },
		],
	},
	{
		id: "education",
		label: "Education",
		industry: "教育培训",
		image: "/images/3ChatClawLandingPage/section3-4.png",
		metrics: [
			{ label: "评论响应时效", value: "+200%", tone: "success" },
			{ label: "漏单率", value: "近乎零漏单", tone: "primary" },
			{ label: "获客规模", value: "无限扩张", tone: "primary" },
		],
	},
];

function ResultCarousel() {
	const logicalLength = RESULT_CASES.length;
	const [virtualIndex, setVirtualIndex] = useState(1); // 默认第二张为居中卡片
	const trackRef = useRef<HTMLDivElement | null>(null);
	const firstCardRef = useRef<HTMLDivElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [cardStep, setCardStep] = useState<number | null>(null);
	const [centerOffset, setCenterOffset] = useState(0);

	useEffect(() => {
		const measure = () => {
			if (!firstCardRef.current || !trackRef.current || !containerRef.current)
				return;
			const cardWidth = firstCardRef.current.offsetWidth;
			// gap-4 => 16px 间距
			const step = cardWidth + 16;
			setCardStep(step);

			const containerWidth = containerRef.current.offsetWidth;
			// 让当前卡片中心与容器中心对齐：偏移为容器宽度减去一张卡片宽度的一半
			setCenterOffset((containerWidth - step + 16) / 2);
		};

		measure();
		window.addEventListener("resize", measure);
		return () => window.removeEventListener("resize", measure);
	}, []);

	// 自动轮播：始终向一个方向滚动
	useEffect(() => {
		const id = window.setInterval(() => {
			setVirtualIndex((prev) => prev + 1);
		}, 4500);

		return () => {
			window.clearInterval(id);
		};
	}, []);

	const handlePrev = () => {
		setVirtualIndex((prev) => Math.max(0, prev - 1));
	};

	const handleNext = () => {
		setVirtualIndex((prev) => prev + 1);
	};

	const activeDotIndex =
		((virtualIndex % logicalLength) + logicalLength) % logicalLength;
	const extendedCases = Array.from({ length: 16 }).flatMap(() => RESULT_CASES);
	const safeIndex = virtualIndex % (logicalLength * 8);

	return (
		<div className="flex flex-col gap-6">
			<div ref={containerRef} className="overflow-hidden">
				<m.div
					ref={trackRef}
					className="flex gap-4"
					animate={
						cardStep != null
							? { x: -safeIndex * cardStep + centerOffset }
							: undefined
					}
					transition={
						cardStep == null
							? { duration: 0 }
							: { type: "spring", stiffness: 90, damping: 18 }
					}
					style={{ willChange: "transform" }}
				>
					{extendedCases.map((item, index) => (
						<div
							key={item.id}
							ref={index === 0 ? firstCardRef : undefined}
							className="flex-shrink-0"
						>
							<div className="mx-auto w-[260px] sm:w-[300px] md:w-[340px] lg:w-[352px]">
								<ResultCard item={item} />
							</div>
						</div>
					))}
				</m.div>
			</div>

			<div className="flex items-center justify-center gap-4">
				<button
					type="button"
					onClick={handlePrev}
					className="bg-default-100/40 border-default-100/60 hover:bg-default-100/60 flex h-9 w-9 items-center justify-center rounded-full border text-default-500 transition-colors cursor-pointer"
					aria-label="上一行业案例"
				>
					<span className="text-xs">←</span>
				</button>

				<div className="flex items-center gap-2">
					{RESULT_CASES.map((item, index) => (
						<button
							key={item.id}
							type="button"
							onClick={() =>
								setVirtualIndex((prev) => {
									const base = Math.floor(prev / logicalLength) * logicalLength;
									return base + index;
								})
							}
							className={`h-1.5 rounded-full transition-all cursor-pointer ${
								index === activeDotIndex
									? "bg-primary w-10"
									: "bg-default-200/40 w-4"
							}`}
							aria-label={item.industry}
						/>
					))}
				</div>

				<button
					type="button"
					onClick={handleNext}
					className="bg-default-100/40 border-default-100/60 hover:bg-default-100/60 flex h-9 w-9 items-center justify-center rounded-full border text-default-500 transition-colors cursor-pointer"
					aria-label="下一行业案例"
				>
					<span className="text-xs">→</span>
				</button>
			</div>
		</div>
	);
}

function ResultCard({ item }: { item: ResultCase }) {
	const valueToneClassMap: Record<
		ResultCase["metrics"][number]["tone"],
		string
	> = {
		danger: "text-danger-400",
		success: "text-success-400",
		primary: "text-primary",
		secondary: "text-warning-400",
	};

	return (
		<Card className="bg-default-100/10 border-default-100/60 relative flex h-full flex-col overflow-hidden rounded-2xl border">
			<div className="relative h-44 w-full sm:h-52">
				<Image
					removeWrapper
					alt={item.industry}
					className="h-full w-full object-cover"
					src={item.image}
				/>
				<div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-background/10 to-background/90" />

				<div className="absolute left-3 top-3 rounded-md bg-black/35 px-2 py-1 text-[11px] text-default-200">
					{item.label}
				</div>
			</div>

			<CardBody className="bg-[#1e292d] flex flex-1 flex-col gap-3 px-4 py-4 sm:px-5 sm:py-5">
				<p className="text-foreground text-sm font-semibold sm:text-base">
					{item.industry}
				</p>
				{item.metrics.map((metric) => (
					<div
						key={metric.label}
						className="flex items-center justify-between text-xs sm:text-[13px]"
					>
						<span className="text-default-500">{metric.label}</span>
						<span className={`font-semibold ${valueToneClassMap[metric.tone]}`}>
							{metric.value}
						</span>
					</div>
				))}
			</CardBody>
		</Card>
	);
}

function RiskCard({
	tone,
	title,
	subtitle,
	description,
	bullets,
}: RiskCardProps) {
	const toneColorMap: Record<RiskCardProps["tone"], string> = {
		danger: "border-danger-400/60",
		primary: "border-primary/60",
		success: "border-success-400/60",
	};

	const toneSoftBgMap: Record<RiskCardProps["tone"], string> = {
		danger: "bg-danger/10 text-danger-400",
		primary: "bg-primary/10 text-primary",
		success: "bg-success/10 text-success-400",
	};

	return (
		<m.div
			whileHover={{ y: -4 }}
			transition={{ type: "spring", stiffness: 260, damping: 20 }}
			style={{ willChange: "transform" }}
		>
			<Card
				className={`bg-default-100/10 border-default-100/60 relative h-full overflow-hidden rounded-2xl border`}
			>
				<CardBody className="relative flex flex-col gap-5 px-6 py-6 sm:px-7 sm:py-7">
					<div className="flex flex-col items-start gap-3 text-left">
						<div
							className={`inline-flex items-center gap-2 rounded-xl px-3 py-1 text-[11px] font-medium ${toneSoftBgMap[tone]}`}
						>
							<span className="h-1.5 w-1.5 rounded-full bg-current" />
							<span>{subtitle}</span>
						</div>
						<div>
							<p className="text-foreground text-lg font-semibold sm:text-xl">
								{title}
							</p>
							<p className="text-default-500 mt-1 text-xs sm:text-sm">
								{description}
							</p>
						</div>
					</div>

					<div className="bg-default-100/10 border-default-100/40 mt-2 flex flex-col gap-2 rounded-xl border px-3.5 py-3">
						{bullets.map((line) => (
							<div
								key={line}
								className="flex items-start gap-2 text-xs sm:text-[13px]"
							>
								<span className="mt-[6px] h-1 w-1 flex-shrink-0 rounded-full bg-default-400" />
								<span className="text-default-500">{line}</span>
							</div>
						))}
					</div>
				</CardBody>
			</Card>
		</m.div>
	);
}
